/**
 * Buildings registry — single source of truth.
 *
 * Lives in `shared/` so both server (gameplay, costs, footprint validation,
 * combat HP) and client (mesh, placement preview, Z menu icons) read the same
 * row. Adding a new building = adding one row here. If you find yourself
 * editing a second file that lists buildings, you've hit a leak — fix the
 * leak. See `docs/build/data-driven-features.md` for the pattern.
 *
 * Source: `docs/design/16-buildings.md` carries the canonical KTS-verbatim
 * costs / HP / footprints with `MapScript.galaxy` + `UnitData.xml` line
 * citations. Costs come from `gf_Build*` functions (authoritative); tooltip
 * costs in `GameStrings.txt` are sometimes stale placeholders.
 *
 * Phase A: Tent, Campfire, Palisade (shelter / warmth / wall — first vertical).
 * Phase B: Forge, Workbench, HuntersLodge, StorageOutpost, Tavern (craft /
 *   shop / storage placeholders — left-click opens the building's menu).
 * Phase C: Farm, AutomatedDefenseTurret, SpikeTrap (production + active defense).
 * Phase D: Wonder (objective / win-condition).
 *
 * Mesh availability: all 12 buildings now have FBX placeholders under
 * `game/client/public/assets/buildings/`. Phase 2 mesh pass (2026-04-29) filled
 * the last four (Workbench=MarketStand_1, Palisade=Fence, Farm=Stable,
 * SpikeTrap=BearTrap_Open). The primitive-fallback path stays as a safety net
 * if FBX load ever fails.
 *
 * v1 deviations from KTS captured in `16-buildings.md`:
 *   - Tent: drop `Untargetable=1` so destroying enemy camps stays interactive.
 *   - Palisade: 1L per segment (no LumberParts half-stack accounting).
 *   - SpikeTrap: 1 HP + invulnerable flag substitutes for `PreventDestroy=1`.
 *   - Wonder: +1 Iron Ore design-side gate on top of KTS's 10L+6S.
 */

export type BuildingType =
  | "Tent" | "Campfire" | "Palisade"
  | "Forge" | "Workbench" | "HuntersLodge" | "StorageOutpost"
  | "Farm" | "Tavern"
  | "AutomatedDefenseTurret" | "SpikeTrap"
  | "Wonder";

export type BuildingRole =
  | "shelter"      // Tent
  | "warmth"       // Campfire
  | "wall"         // Palisade
  | "craft"        // Forge, Workbench, HuntersLodge
  | "shop"         // Tavern
  | "storage"      // StorageOutpost
  | "production"   // Farm
  | "turret"       // ADT (auto-fire weapon)
  | "trap"         // SpikeTrap (proximity)
  | "objective";   // Wonder

export interface BuildingCost {
  lumber?: number;
  stone?: number;
  ironOre?: number;
  /** Mix-match slot: total of (wool + leather) must satisfy this. Tent=1, StorageOutpost=2.
      KTS first checks Wool, falls back to Leather; v1 server validates the sum. */
  woolOrLeather?: number;
}

export interface Footprint {
  /** Cell extents on the X-Z grid. 1x1=Palisade/ADT, 2x2=Campfire/Storage/SpikeTrap,
      3x2=Forge, 1x2=Workbench, 3x3=Tavern/Lodge/Farm, 5x4=Wonder. */
  cellsX: number;
  cellsZ: number;
  /** True if enemies cannot path through (Palisade only — KTS `Footprint1x1ContourNoLOS`).
      ADT carries `EnemyPathingBlocker` separately; we model that via `blocksPath` below. */
  blocksEnemies: boolean;
}

export interface BuildingDef {
  type: BuildingType;
  /** FBX asset URL where available; null = client renders a primitive fallback. */
  meshUrl: string | null;
  /** Visual scale to bring native FBX size in line with the footprint. Hand-tuned
      per mesh (Quaternius / Synty packs ship at varying native units). */
  scale: number;
  role: BuildingRole;
  cost: BuildingCost;
  /** Final HP after construction completes (KTS `LifeMax`). */
  hpMax: number;
  /** Physical armor; reduces incoming attack damage via the armor formula in
      `combat.ts`. Verbatim from KTS `LifeArmor`. */
  armor: number;
  footprint: Footprint;
  /** Construction wind-up in seconds. Default 25 (KTS `RepairTime`); Wonder is
      30 (KTS `KoboldTestBuild,Build17 Time="30"` — the deliberate long build is
      the window where opponents race to also build their Wonder). */
  buildSec: number;
  /** XP awarded to the builder on completion. */
  xp: number;
  /** True if the owner can salvage for a 50% refund (KTS `Salvagable` behavior).
      Campfire / SpikeTrap / Wonder = false (one-way placements / objective). */
  salvagable: boolean;
  /** True if the building blocks pathfinding (carved into navmesh-equivalent
      obstacle list). Palisade is also `blocksEnemies` (one-sided). */
  blocksPath: boolean;
  /** Sight units at day; KTS `SightBonus[Dusk]=-2` is applied at runtime in
      vision.ts. 0 = no vision (Palisade, Wonder). */
  sight: number;
}

// === Phase A: shelter / warmth / wall ===

/**
 * Tent — Stamina-regen aura + rain shelter.
 *   UnitData.xml:9966 (LifeMax=150, Sight=6)
 *   gf_BuildTent (MapScript.galaxy:21687): 1 Lumber + (1 Wool OR 1 Leather)
 *   Behaviors: Shelter (period 0.125s, ShelterSet aura) + Resting (per-kobold
 *     stamina-regen buff applied to nearby allies).
 *   Mesh scale 0.002 → ~2.9 m × 2.0 m × 5.0 m kobold-tent silhouette. The
 *   Tent.fbx group bounding box is huge (~1458 × 1032 × 2487 native units), so
 *   it needs a tighter divisor than the 0.012 baseline shared by Forge/Tavern.
 *   v1 DEVIATION: drops KTS's `Untargetable=1` flag so enemy camps can be
 *   destroyed (gameplay-interactive); HP unchanged.
 */
const TENT: BuildingDef = {
  type: "Tent",
  meshUrl: "/assets/buildings/Tent_1A.fbx",
  scale: 0.0074, // EXPLORER Tent_1A native 498×298×538 → ~3.7×2.2×4.0m, fills the 4×4 square
  role: "shelter",
  cost: { lumber: 1, woolOrLeather: 1 },
  hpMax: 150, armor: 0,
  footprint: { cellsX: 4, cellsZ: 4, blocksEnemies: false },
  buildSec: 25, xp: 50,
  salvagable: true, blocksPath: false, sight: 6,
};

/**
 * Campfire — Cooking + warmth.
 *   UnitData.xml:10006 (unlit) / :9467 (lit, LifeMax=200, LifeRegenRate=-1 = decay 1 HP/s)
 *   gf_BuildCampfire (MapScript.galaxy:21725): 1 Lumber + 1 Stone
 *   Behaviors (lit): Burning (HeatSearch period 0.5s applies Heat buff) + FireWard
 *     (extinguishes nearby Frostbite). Refresh via lumber-burn or expert ability.
 *   NOT salvagable — campfires are a one-way placement per KTS.
 *   Mesh scale 0.012 → small fire pit silhouette.
 */
const CAMPFIRE: BuildingDef = {
  type: "Campfire",
  meshUrl: "/assets/buildings/Campfire_01.fbx",
  scale: 1.5, // RedWorks Campfire_01 native ~0.67m → ~1m
  role: "warmth",
  cost: { lumber: 1, stone: 1 },
  hpMax: 200, armor: 0,
  footprint: { cellsX: 2, cellsZ: 2, blocksEnemies: false },
  buildSec: 25, xp: 50,
  salvagable: false, blocksPath: false, sight: 6,
};

/**
 * Palisade — Defensive wall segment, hardest armor in v1.
 *   UnitData.xml:6186 (LifeMax=1000, LifeArmor=5)
 *   gf_BuildPalisade (MapScript.galaxy:22097-22128): 1 Lumber per segment.
 *   Behaviors: Salvagable + Fortified (passive armor bonus on top of LifeArmor=5).
 *   Footprint1x1ContourNoLOS — only 1×1 building that blocks LOS. Walls funnel sightlines.
 *   No mesh in current asset pack — client renders a primitive box (~1 cell tall).
 *   `blocksEnemies: true` is the one-sided pathing block (KTS wall-snap).
 *   v1 SIMPLIFICATION: drops KTS `LumberParts` half-stack trick (1L → 2 segments
 *   via change-stacks). v1 is a flat 1L → 1 segment for HUD simplicity.
 *   `CreatePalisadeGate` cut for v1 — walls don't have promotable gates.
 */
const PALISADE: BuildingDef = {
  type: "Palisade",
  meshUrl: "/assets/buildings/WoodenFence.fbx",
  // RedWorks WoodenFence native ~3.23m tall; scale 0.68 → ~2.2m palisade section.
  scale: 0.68,
  role: "wall",
  cost: { lumber: 1 },
  hpMax: 1000, armor: 5,
  footprint: { cellsX: 1, cellsZ: 1, blocksEnemies: true },
  buildSec: 25, xp: 50,
  salvagable: true, blocksPath: true, sight: 0,
};

// === Phase B: craft / shop / storage ===

/**
 * Forge — Metal weapon + armor crafting bench (KTS `Smithy`).
 *   UnitData.xml:9822 (LifeMax=200, LifeArmor=1, Sight=6)
 *   gf_BuildSmithy (MapScript.galaxy:21857): 1 Lumber + 1 Stone + 1 Leather
 *   Footprint3x2. Hosts the SmithyForge train list (15 indexed Train slots) +
 *   dedicated Forge* abilities split across Main-hand / Offhand / Armor submenus.
 *   v1 ships placeholder left-click menu; full recipe trainer is a Phase B+ port.
 *   Mesh scale 0.012 → matches the Tent/Campfire size baseline.
 */
const FORGE: BuildingDef = {
  type: "Forge",
  meshUrl: "/assets/buildings/Furnace_1C.fbx",
  scale: 0.9, // EXPLORER Furnace_1C native 3.38×2.15×3.37 → ~3.0×1.9×3.0m, fits the 3×2 square
  role: "craft",
  cost: { lumber: 1, stone: 1, woolOrLeather: 1 },
  hpMax: 200, armor: 1,
  footprint: { cellsX: 3, cellsZ: 2, blocksEnemies: false },
  buildSec: 25, xp: 80,
  salvagable: true, blocksPath: true, sight: 6,
};

/**
 * Workbench — Wood / leather / cloth / bone crafting bench.
 *   UnitData.xml:10059 (LifeMax=100, no LifeArmor)
 *   gf_BuildWorkbench (MapScript.galaxy:21824): 1 Lumber + 1 Leather
 *   Footprint1x2 — small. KTS gates higher-tier crafts behind workbench
 *   tier-up (Workbench2/22/3); v1 ships single-tier per spec.
 *   No FBX in current asset pack — client renders a primitive box.
 *   Lowest HP of the crafting buildings — easy push target.
 */
const WORKBENCH: BuildingDef = {
  type: "Workbench",
  meshUrl: "/assets/buildings/Workbench_1A.fbx",
  scale: 0.0035, // EXPLORER Workbench_1A native 441×235×564 → ~1.5×0.8×2.0m, fits the 1×2 square
  role: "craft",
  cost: { lumber: 1, woolOrLeather: 1 },
  hpMax: 100, armor: 0,
  footprint: { cellsX: 1, cellsZ: 2, blocksEnemies: false },
  buildSec: 25, xp: 50,
  salvagable: true, blocksPath: true, sight: 6,
};

/**
 * Hunter's Lodge — Pet-mastery upgrade hub + pet-related crafts.
 *   UnitData.xml:7852 (LifeMax=100, no LifeArmor, Sight=6)
 *   gf_BuildLodge (MapScript.galaxy:22011): 2 Lumber + 2 Leather
 *   Footprint3x3 (FootprintTavern shape — same as Tavern + Farm).
 *   Hosts the seven canonical pet upgrades (ThickHide, InspirationalMaster,
 *   NaturalTenacity, Companionship, PredatoryKnowledge, Viciousness, Loyalty)
 *   plus pet-craft abilities. Pet upgrades persist on the player even if the
 *   lodge dies — destruction stops *further* upgrades + Loyalty respawn.
 *   On finish-construction, KTS gf_HuntersLodgeSpawn writes the player name
 *   to the unit info-text plate (cosmetic; v1 may skip).
 */
const HUNTERS_LODGE: BuildingDef = {
  type: "HuntersLodge",
  meshUrl: "/assets/buildings/Hut_2A.fbx",
  scale: 0.0055, // EXPLORER Hut_2A native ~558×531×550 → ~3.0×2.9×3.0m, fits the 3×3 square
  role: "craft",
  cost: { lumber: 2, woolOrLeather: 2 },
  hpMax: 100, armor: 0,
  footprint: { cellsX: 3, cellsZ: 3, blocksEnemies: false },
  buildSec: 25, xp: 120,
  salvagable: true, blocksPath: true, sight: 6,
};

/**
 * Storage Outpost — Player-to-player resource market + stockpile.
 *   UnitData.xml:9155 (LifeMax=350, LifeArmor=2 — toughest non-Wonder structure)
 *   gf_BuildOutpost (MapScript.galaxy:21928): 3 Lumber + 2 (Wool|Leather, mix-match)
 *   Footprint2x2Contour. Hosts Sell-x / Buy-x abilities for cross-tribe trade
 *   (Profiteering owner-kickback CUT for v1). LockStorage denies enemy buyers.
 *   v1 trades the full KTS-8 roster via a Normal|Rare tabbed menu (Lumber/Stone/
 *   Wool/Leather + Iron Ore/Shadowstone/Radiant Gem/Infused Lumber) — see
 *   shared/trade.ts. Prices KTS-faithful.
 *   Mesh: KTS Storage.fbx placeholder.
 */
const STORAGE_OUTPOST: BuildingDef = {
  type: "StorageOutpost",
  meshUrl: "/assets/buildings/Stockpile_1A.fbx",
  scale: 0.36, // EXPLORER Stockpile_1A native ~6.2m → ~2.2m
  role: "storage",
  cost: { lumber: 3, woolOrLeather: 2 },
  hpMax: 350, armor: 2,
  footprint: { cellsX: 2, cellsZ: 2, blocksEnemies: false },
  buildSec: 25, xp: 120,
  salvagable: true, blocksPath: true, sight: 6,
};

/**
 * Tavern — Spell shop + consumables + forward respawn anchor.
 *   UnitData.xml:9710 (LifeMax=250, LifeArmor=1)
 *   gf_BuildTavern (MapScript.galaxy:21892): 2 Lumber + 2 Stone
 *   FootprintTavern (placement Footprint3x3). Hosts BuyBeer / BuyRoastedLamb /
 *   BuyCheese / BuyCleansingDraught / BuyCandle + BuySpellBook + Learn* spell
 *   buys (gated behind dusk wizard spawn — gf_TravelingWizards, MapScript.galaxy:18922).
 *   At night, GenericNightBehavior enables wizard interaction; one wizard per tavern
 *   per night since KTS v4.0. Strategically important — destroying enemy taverns
 *   denies forward-respawn + spell access.
 */
const TAVERN: BuildingDef = {
  type: "Tavern",
  meshUrl: "/assets/buildings/Shelter_01.fbx",
  scale: 0.24, // RedWorks Shelter_01 native 10.6×4.4×13.4 → ~2.6×1.0×3.2m, fits the 3×3 square (was 0.80 = way oversized)
  role: "shop",
  cost: { lumber: 2, stone: 2 },
  hpMax: 250, armor: 1,
  footprint: { cellsX: 3, cellsZ: 3, blocksEnemies: false },
  buildSec: 25, xp: 100,
  salvagable: true, blocksPath: true, sight: 6,
};

// === Phase C: production / active defense ===

/**
 * Farm — Wildlife / livestock production hub.
 *   UnitData.xml:9322 (LifeMax=200, LifeArmor=1)
 *   gf_BuildFarm (MapScript.galaxy:21766-21772): 1 Lumber + 1 Stone
 *   FootprintFarm (placement Footprint3x3). Two abilities: FoxBait (renamed in
 *   v1 from KTS PheasantBait per 10-creatures.md §3 Pheasant→Fox reskin) lures
 *   Foxes for passive RawFox drops; DomesticatedSheep converts a wild Sheep into
 *   a tame one that auto-shears Wool over time.
 *   Behaviors: FarmSpawn (per-tick wildlife-spawning aura) + Salvagable.
 *   No FBX in current asset pack — client renders a primitive box.
 *   KTS wires construction into TutorialPart7; v1 drops the tutorial dependency.
 */
const FARM: BuildingDef = {
  type: "Farm",
  meshUrl: "/assets/buildings/Clay_Trough_1A.fbx",
  // EXPLORER Clay_Trough_1A native ~0.53m tall (a low cultivation trough).
  scale: 1.7,
  role: "production",
  cost: { lumber: 1, stone: 1 },
  hpMax: 200, armor: 1,
  footprint: { cellsX: 3, cellsZ: 3, blocksEnemies: false },
  buildSec: 25, xp: 50,
  salvagable: true, blocksPath: true, sight: 6,
};

/**
 * Automated Defense Turret — Auto-firing defensive turret.
 *   UnitData.xml:7214 (LifeMax=250, no LifeArmor); ADTTurret weapon at :7256
 *   gf_BuildADT (MapScript.galaxy:22134-22153): 1 Lumber + 1 Stone + 1 Iron Ore
 *   EnemyPathingBlocker1x1 — enemies cannot path through, allies can (choke-channel).
 *   Weapon: AutomatedDefenseTurret base damage 35 (KTS-verbatim default).
 *   The "35 + (Int+Will)×0.2" Long Night scaling at MapScript.galaxy:8652-8654
 *   is descoped per 10-creatures.md, so v1 ships flat 35 dmg. Auto-fire logic
 *   lives server-side in `turret.ts` (Task 18); registry just carries placement data.
 *   Mesh: Watchtower.fbx (Bell_Tower stand-in for turret silhouette).
 */
const AUTOMATED_DEFENSE_TURRET: BuildingDef = {
  type: "AutomatedDefenseTurret",
  meshUrl: "/assets/buildings/Tower_1A.fbx",
  scale: 0.40, // EXPLORER Tower_1A native ~13.7m → ~5.5m watch tower
  role: "turret",
  cost: { lumber: 1, stone: 1, ironOre: 1 },
  hpMax: 250, armor: 0,
  footprint: { cellsX: 1, cellsZ: 1, blocksEnemies: true },
  buildSec: 25, xp: 50,
  salvagable: true, blocksPath: false, sight: 6,
};

/**
 * Spike Trap — Charge-based proximity damage + stun.
 *   UnitData.xml:9529 (no LifeMax — KTS uses PreventDestroy=1 for invulnerability)
 *   gf_BuildSpikeTrap (MapScript.galaxy:21974-21986): 1 Iron Ore (iron-only)
 *   Damage: 45 × spikeTrapBonusDamage (MapScript.galaxy:8642-8650), 5 charges per
 *   trap (MapScript.galaxy:3571). When charges hit 0, SpikeTrapApplyDull triggers
 *   and SpikeTrapGlow halo shuts off. Sharpen refills charges (ships unconditionally
 *   in v1; full Cooking-expertise gate lands with the perk port). WoolPadding
 *   prevents friendly damage. Sight=4 (limited spotting range, intentional).
 *   Footprint2x2NoCollision — physically 2×2 but enemies pass *through* (overlap fires).
 *   v1 SIMPLIFICATION: 1 HP + invulnerable flag substitutes for KTS PreventDestroy=1.
 *   Charges-only lifecycle. No FBX — client renders a low cone primitive.
 */
const SPIKE_TRAP: BuildingDef = {
  type: "SpikeTrap",
  meshUrl: "/assets/buildings/Spike_Trap_1A.fbx",
  // EXPLORER Spike_Trap_1A native ~111u tall; scale 0.0063 → ~0.7m spikes.
  scale: 0.0063,
  role: "trap",
  cost: { ironOre: 1 },
  hpMax: 1, armor: 0,
  footprint: { cellsX: 2, cellsZ: 2, blocksEnemies: false },
  buildSec: 25, xp: 40,
  salvagable: false, blocksPath: false, sight: 4,
};

// === Phase D: objective ===

/**
 * Wonder — Win-condition structure (KTS `Fortress`).
 *   UnitData.xml:5827 (LifeMax=5000, no LifeArmor)
 *   gf_BuildWatchTower (MapScript.galaxy:22332): 10 Lumber + 6 Stone (KTS-verbatim)
 *   v1 adds +1 Iron Ore as a design-side gate making Wonder construction load-bearing
 *   on the mining loop (Iron now ships in v1 — see 06-economy-gear.md §Resources).
 *   Footprint5x4Contour — largest in v1.
 *   Build time 30s (KoboldTestBuild,Build17 Time="30", AbilData.xml:2636) — the
 *   deliberate long build is the window where opponents race to also build a Wonder.
 *   v1 STRIPS the watchtower dual role: no ballista weapon, no WatchTowerNightLight,
 *   no garrison loadout, no upgrade modules. Pure objective: build it, defend it, win.
 *   Win mechanics (Phase D, port verbatim): first construction starts a 300s timer;
 *   any team without a Wonder by expiry is defeated. One-shot extension if a Wonder
 *   is in-progress at expiry (gf_LeastConstructionProgress + 30.0 buffer). Destroying
 *   a built Wonder = immediate team defeat (MapScript.galaxy:52242-52247). Logic
 *   lives in `wonder-timer.ts` + `match.ts` (Tasks 20-21).
 *   sight=0 — Wonder grants no vision in v1 (KTS WatchTowerNightLight is descoped).
 */
const WONDER: BuildingDef = {
  type: "Wonder",
  meshUrl: "/assets/buildings/Rock_Totem_1A.fbx",
  scale: 1.95, // EXPLORER Rock_Totem_1A native ~2.3m → ~4.5m monument
  role: "objective",
  cost: { lumber: 10, stone: 6, ironOre: 1 },
  hpMax: 5000, armor: 0,
  footprint: { cellsX: 5, cellsZ: 4, blocksEnemies: false },
  buildSec: 30, xp: 200,
  salvagable: false, blocksPath: true, sight: 0,
};

export const BUILDING_DEFS: Record<BuildingType, BuildingDef> = {
  Tent: TENT,
  Campfire: CAMPFIRE,
  Palisade: PALISADE,
  Forge: FORGE,
  Workbench: WORKBENCH,
  HuntersLodge: HUNTERS_LODGE,
  StorageOutpost: STORAGE_OUTPOST,
  Tavern: TAVERN,
  Farm: FARM,
  AutomatedDefenseTurret: AUTOMATED_DEFENSE_TURRET,
  SpikeTrap: SPIKE_TRAP,
  Wonder: WONDER,
};
